import { getTzAdjustedStartOfDay } from '@govinity/shared-config';
import { z } from 'zod';
export * from './agendas/index.js';
export * from './documents/index.js';
export * from './live/index.js';
export * from './minutes/index.js';
export * from './public-comments/index.js';
import { createSortablePaginationSchema, createStringFilterSchema, dateFilterSchema, idFilterSchema, } from '../shared/index.js';
export var MeetingStatus;
(function (MeetingStatus) {
    MeetingStatus["Upcoming"] = "upcoming";
    MeetingStatus["Live"] = "live";
    MeetingStatus["Completed"] = "completed";
    MeetingStatus["Cancelled"] = "cancelled";
})(MeetingStatus || (MeetingStatus = {}));
/**
 * Resolves the status a meeting should be presented as, which is not always the stored one.
 *
 * `live`, `completed` and `cancelled` are recorded facts and always pass through untouched. Only
 * `upcoming` is an expectation about the schedule: clerks routinely never open the console for a
 * meeting, so the stored status stays `upcoming` forever while the calendar has moved on. Every
 * surface that shows a status to a human runs it through here, so the internal app and the public
 * portal tell citizens and staff the same thing about a meeting that has already come and gone.
 *
 * The cutoff is the start of the current day in the government's timezone, the same boundary the
 * meetings lists use to split upcoming from past, so a meeting held earlier today keeps reading
 * upcoming until the day is over. The stored status is untouched and still drives the live-meeting,
 * cancellation and speaker-signup workflows.
 *
 * @param date - The meeting's scheduled date.
 * @param status - The stored status.
 * @param timezone - The government's timezone, which owns the day boundary.
 * @returns The status to display.
 */
export const getDisplayMeetingStatus = ({ date, status, timezone, }) => {
    if (status !== MeetingStatus.Upcoming) {
        return status;
    }
    const hasPassed = new Date(date) < getTzAdjustedStartOfDay(new Date(), timezone);
    return hasPassed ? MeetingStatus.Completed : status;
};
export const DEFAULT_STREAM_DURATION_SECONDS = 3 * 60 * 60;
export const DEFAULT_STREAM_ENABLEMENT_LEAD_SECONDS = 600;
export const DEFAULT_AUTO_PROMOTE_LEAD_SECONDS = 120;
const MAX_STREAM_LEAD_TIME_SECONDS = 59 * 60;
const streamControlSchemaFields = {
    mediaEncoderId: z.number().nullish(),
    streamDurationSeconds: z.number().int().positive().nullish(),
    autoStart: z.boolean().optional(),
    streamEnablementLeadSeconds: z.number().int().min(0).max(MAX_STREAM_LEAD_TIME_SECONDS).nullish(),
    autoPromoteLeadSeconds: z.number().int().min(0).max(MAX_STREAM_LEAD_TIME_SECONDS).nullish(),
};
const applyStreamDefaults = (data) => {
    const hasStreamControlInput = data.mediaEncoderId !== undefined ||
        data.streamDurationSeconds !== undefined ||
        data.autoStart !== undefined ||
        data.streamEnablementLeadSeconds !== undefined ||
        data.autoPromoteLeadSeconds !== undefined;
    if (!hasStreamControlInput) {
        return data;
    }
    if (!data.mediaEncoderId) {
        return {
            ...data,
            streamDurationSeconds: null,
            autoStart: false,
            streamEnablementLeadSeconds: null,
            autoPromoteLeadSeconds: null,
        };
    }
    if (!data.autoStart) {
        return {
            ...data,
            streamDurationSeconds: data.streamDurationSeconds ?? DEFAULT_STREAM_DURATION_SECONDS,
            autoStart: false,
            streamEnablementLeadSeconds: null,
            autoPromoteLeadSeconds: null,
        };
    }
    const streamEnablementLeadSeconds = data.streamEnablementLeadSeconds ?? DEFAULT_STREAM_ENABLEMENT_LEAD_SECONDS;
    const autoPromoteLeadSeconds = data.autoPromoteLeadSeconds ?? DEFAULT_AUTO_PROMOTE_LEAD_SECONDS;
    return {
        ...data,
        streamDurationSeconds: data.streamDurationSeconds ?? DEFAULT_STREAM_DURATION_SECONDS,
        autoStart: true,
        streamEnablementLeadSeconds,
        autoPromoteLeadSeconds,
    };
};
const refineStreamLeadTimes = (data) => {
    if (!data.autoStart) {
        return true;
    }
    const streamEnablementLeadSeconds = data.streamEnablementLeadSeconds ?? DEFAULT_STREAM_ENABLEMENT_LEAD_SECONDS;
    const autoPromoteLeadSeconds = data.autoPromoteLeadSeconds ?? DEFAULT_AUTO_PROMOTE_LEAD_SECONDS;
    return autoPromoteLeadSeconds <= streamEnablementLeadSeconds;
};
const refineStreamLeadTimesMessage = {
    message: 'Auto-promote lead time must be less than or equal to stream enablement lead time',
    path: ['autoPromoteLeadSeconds'],
};
// Query parameters schema for filtering meetings
export const MEETING_EVENT_ORDER_BY_FIELDS = [
    'createdAt',
    'date',
    'location',
    'status',
    'title',
];
export const searchMeetingEventsQuerySchema = createSortablePaginationSchema(MEETING_EVENT_ORDER_BY_FIELDS).extend({
    id: idFilterSchema.optional(),
    agendaTemplateId: idFilterSchema.optional(),
    meetingBodyId: idFilterSchema.optional(),
    date: dateFilterSchema.optional(),
    status: createStringFilterSchema(z.enum(MeetingStatus)).optional(),
    hasPublishedAgenda: z.boolean().optional(),
    hasRecording: z.boolean().optional(),
    AND: z
        .array(z.object({
        meetingBodyId: idFilterSchema.optional(),
        date: dateFilterSchema.optional(),
        status: createStringFilterSchema(z.enum(MeetingStatus)).optional(),
    }))
        .optional(),
    OR: z
        .array(z.object({
        agendaTemplateId: idFilterSchema.optional(),
        meetingBodyId: idFilterSchema.optional(),
        date: dateFilterSchema.optional(),
        status: createStringFilterSchema(z.enum(MeetingStatus)).optional(),
        id: idFilterSchema.optional(),
    }))
        .optional(),
});
// hasRecording counts unpublished archives, so it stays internal-only.
export const publicSearchMeetingEventsQuerySchema = searchMeetingEventsQuerySchema
    .omit({ hasRecording: true })
    .extend({
    governmentCode: z.string().min(1, 'Government code is required').optional(),
});
const baseObjectSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    location: z.string().nullish(),
    locationId: z.number().nullish(),
    meetingBodyId: z.number('Select a meeting body').min(1, 'Select a meeting body').nullish(),
    jointMeetingBodyId: z.number().nullish(),
    agendaTemplateId: z.number().nullish(),
    isPrivate: z.boolean().optional(),
    virtualMeetingUrl: z.url('Must be a valid URL').nullish(),
    meetingDialIn: z.string().nullish(),
    meetingVirtualId: z.string().nullish(),
    clerkUserId: z.number().nullish(),
    autoPublishMediaArchive: z
        .boolean()
        .optional()
        .describe("Omit on create to inherit the meeting body's (or joint meeting body's) setting."),
    ...streamControlSchemaFields,
});
const refineLocation = (data) => Boolean(data.location || data.locationId);
const refineLocationMessage = {
    message: 'Select a location from the list or enter one manually',
    path: ['location'],
};
const refineMeetingBody = (data) => Boolean(data.meetingBodyId || data.jointMeetingBodyId);
const refineMeetingBodyMessage = {
    message: 'Select a meeting body or joint meeting body',
    path: ['meetingBodyId', 'jointMeetingBodyId'],
};
export const baseSchema = baseObjectSchema
    .refine(refineLocation, refineLocationMessage)
    .refine(refineMeetingBody, refineMeetingBodyMessage)
    .refine(refineStreamLeadTimes, refineStreamLeadTimesMessage);
export const frontendMeetingEventCreateSchema = baseSchema
    .safeExtend({
    date: z.date('Meeting date is required'),
    time: z.date('Meeting time is required'),
})
    .transform((data) => {
    // Set location to null if locationId has a valid value
    if (data.locationId !== null && data.locationId !== undefined) {
        return {
            ...data,
            location: null,
        };
    }
    return data;
})
    .transform(applyStreamDefaults);
export const meetingEventCancelSchema = z.object({
    cancelReason: z.string().min(1, 'A cancellation reason is required'),
});
/**
 * Body for `POST /meeting-events/{id}/cancel-sibling-auto-start`, where `{id}` is the meeting the
 * clerk is running: names the auto-start sibling on the same encoder to stand down (GOV-1727).
 */
export const cancelSiblingAutoStartSchema = z.object({
    conflictMeetingEventId: z.number().int().positive(),
});
/**
 * Body for `POST /meeting-events/{id}/set-complete`. Adjourn resolves the broadcast by encoder and, by
 * default, ends it when this is the last live participant (else parks it for meetings still in recess).
 * `keepStreaming` is set for **Adjourn & switch**: the clerk is continuing on another meeting, so the
 * broadcast is parked off-air (kept alive) even when this is the last participant, and the destination's
 * Call to Order reclaims it. Standalone adjourn omits it and ends the stream.
 */
export const meetingEventSetCompleteSchema = z.object({
    keepStreaming: z.boolean().optional(),
});
/**
 * Body for `POST /meeting-events/{id}/set-live`. `startOffAir` lets a clerk deliberately start the
 * meeting *without* taking over a broadcast already airing another meeting on its encoder: the meeting
 * goes Live but off-air (the stream keeps airing the other meeting) instead of claiming it. Absent/false
 * is the default — an active broadcast on the encoder is claimed (GOV-1720).
 */
export const meetingEventSetLiveSchema = z.object({
    startOffAir: z.boolean().optional(),
});
/**
 * Body for `POST /meeting-events/{id}/reconvene`. Only matters when the meeting went off air by choice
 * before the recess: `onAir: true` also brings the stream back; omitted keeps it off air.
 */
export const meetingEventReconveneSchema = z.object({
    onAir: z.boolean().optional(),
});
export const meetingEventExtendDurationSchema = z.object({
    additionalSeconds: z.number().int().positive('Additional seconds must be greater than 0'),
});
export const meetingEventCreateSchema = baseSchema
    .safeExtend({
    date: z.iso.datetime('Meeting date is required'),
    createAssociatedItems: z.boolean().optional(),
})
    .transform((data) => {
    // Set location to null if locationId has a valid value
    if (data.locationId !== null && data.locationId !== undefined) {
        return {
            ...data,
            location: null,
        };
    }
    return data;
})
    .transform(applyStreamDefaults);
/**
 * Refinement for meeting body fields in update schema.
 * Allows updates without meeting body fields (both undefined).
 * Only requires a meeting body when at least one field is explicitly set.
 */
const refineMeetingBodyForUpdate = (data) => {
    // If neither field is provided (both undefined), allow it - existing values will be kept
    if (data.meetingBodyId === undefined && data.jointMeetingBodyId === undefined) {
        return true;
    }
    // If at least one is provided, at least one must be truthy
    return Boolean(data.meetingBodyId || data.jointMeetingBodyId);
};
export const meetingEventUpdateSchema = baseObjectSchema
    .omit({ meetingBodyId: true, jointMeetingBodyId: true })
    .extend({
    date: z.iso.datetime('Meeting date is required'),
    jointMeetingBodyId: z.number().nullish(),
    meetingBodyId: z.number().nullish(),
    // Actual call-to-order time. Distinct from the scheduled `date`: the API only honors this on a
    // meeting that is already completed (enforced in the meeting-events controller). It is a
    // display/history correction and must never rewrite broadcast or media-timeline clocks.
    startedAt: z.iso.datetime('Actual start time must be a valid date').nullish(),
})
    .refine(refineLocation, refineLocationMessage)
    .refine(refineMeetingBodyForUpdate, refineMeetingBodyMessage)
    .refine(refineStreamLeadTimes, refineStreamLeadTimesMessage)
    .transform((data) => {
    // Set location to null if locationId has a valid value
    if (data.locationId !== null && data.locationId !== undefined) {
        return {
            ...data,
            location: null,
        };
    }
    return data;
})
    .transform(applyStreamDefaults);
/** Shared message for the "actual start cannot be after adjournment" rule. */
export const ACTUAL_START_AFTER_COMPLETION_MESSAGE = 'Actual start time cannot be after the meeting was completed';
/**
 * Whether a proposed actual start (call-to-order) time is invalid because it falls after the meeting
 * adjourned. The server update guard and the web form share this so the rule stays in one place — the
 * check itself can't live in {@link meetingEventUpdateSchema} because `completedAt` is server-owned
 * state, not part of the update payload.
 */
export const isActualStartAfterCompletion = (startedAt, completedAt) => new Date(startedAt).getTime() > new Date(completedAt).getTime();
/**
 * Payload for copying an existing meeting event. The new date is the only accepted input —
 * every other field is taken verbatim from the source meeting.
 */
export const meetingEventCopySchema = z.object({
    date: z.iso.datetime('Meeting date is required'),
});
export const meetingEventScheduleSchema = z
    .object({
    firstMeetingDate: z.preprocess((value) => (typeof value === 'string' ? new Date(value) : value), z.date('First meeting date is required')),
    endDate: z
        .preprocess((value) => (typeof value === 'string' ? new Date(value) : value), z.date())
        .optional(),
    duration: z.number('Please select a duration'),
    agendaTemplateId: z.number().nullish(),
    meetingBodyId: z.number('Select a meeting body').min(1, 'Select a meeting body').nullish(),
    jointMeetingBodyId: z.number().nullish(),
    recurringInterval: z.string().nullish(),
    recurringDay: z.string().nullish(),
    recurringDescription: z.string().nullish(),
    mediaEncoderId: z.number().nullish(),
    streamDurationSeconds: z.number().int().positive().nullish(),
    autoStart: z.boolean().optional(),
    streamEnablementLeadSeconds: z.number().int().min(0).nullish(),
    autoPromoteLeadSeconds: z.number().int().min(0).nullish(),
    recurringTime: z.string('Please select a value').refine((time) => {
        if (!time)
            return true;
        const [hours, minutes] = time.split(':').map(Number);
        return (typeof hours === 'number' &&
            typeof minutes === 'number' &&
            hours >= 0 &&
            hours < 24 &&
            minutes >= 0 &&
            minutes < 60);
    }, {
        message: 'Invalid time format. Please use HH:MM format.',
    }),
    locationId: z.number().nullish(),
    location: z.string().nullish(),
    virtualMeetingUrl: z.url('Must be a valid URL').nullable().optional(),
    meetingDialIn: z.string().nullable().optional(),
    meetingVirtualId: z.string().nullable().optional(),
    autoPublishMediaArchive: z.boolean().optional(),
})
    .refine((data) => {
    return data.location || data.locationId;
}, {
    message: 'Select a location from the list or enter one manually',
    path: ['location'],
})
    .refine((data) => data.meetingBodyId || data.jointMeetingBodyId, {
    message: 'Select a meeting body or joint meeting body',
    path: ['meetingBodyId', 'jointMeetingBodyId'],
})
    .refine(refineStreamLeadTimes, refineStreamLeadTimesMessage)
    .refine((data) => {
    const hasRecurringDescription = data.recurringDescription && data.recurringDescription.trim().length > 0;
    const hasRecurringIntervalAndDay = data.recurringInterval &&
        data.recurringInterval.trim().length > 0 &&
        data.recurringDay &&
        data.recurringDay.trim().length > 0;
    return hasRecurringDescription || hasRecurringIntervalAndDay;
}, {
    message: 'Either specify a custom frequency description or select both frequency and day',
    path: ['recurringDescription'],
})
    .refine((data) => data.duration !== 0 || !!data.endDate, {
    message: 'Select an end date',
    path: ['endDate'],
})
    .refine((data) => data.duration !== 0 || !data.endDate || data.endDate > data.firstMeetingDate, {
    message: 'End date must be after the first meeting date',
    path: ['endDate'],
})
    .transform(applyStreamDefaults);
export const meetingEventBulkCreateSchema = z.array(meetingEventCreateSchema);
export const mediaArchiveThumbnailModeSchema = z.enum(['upload', 'frame-time']);
export const mediaArchiveTrimSchema = z.object({
    trimEndSeconds: z.number().min(0, 'Trim end must be 0 or greater'),
    trimStartSeconds: z.number().min(0, 'Trim start must be 0 or greater'),
});
export const mediaArchiveThumbnailPutSignedUrlSchema = z.object({
    name: z.string().min(1, 'Name is required').max(255),
});
export const mediaArchiveThumbnailUploadUpdateSchema = z.object({
    thumbnailMode: z.literal('upload'),
    filename: z.string().min(1, 'Filename is required').max(255),
    key: z.string().min(1, 'Attachment key is required'),
    name: z.string().min(1, 'Name is required').max(255),
});
export const mediaArchiveThumbnailFrameTimeUpdateSchema = z.object({
    thumbnailMode: z.literal('frame-time'),
    thumbnailTimeSeconds: z.number().min(0, 'Thumbnail time must be 0 or greater'),
});
export const mediaArchiveThumbnailClearUpdateSchema = z.object({
    thumbnailMode: z.literal('clear'),
});
export const mediaArchiveThumbnailUpdateSchema = z.discriminatedUnion('thumbnailMode', [
    mediaArchiveThumbnailClearUpdateSchema,
    mediaArchiveThumbnailUploadUpdateSchema,
    mediaArchiveThumbnailFrameTimeUpdateSchema,
]);
/** A 3-hour meeting's captions are a few hundred KB. */
export const MEDIA_ARCHIVE_CAPTIONS_MAX_LENGTH = 2_000_000;
export const mediaArchiveCaptionsUploadSchema = z.object({
    content: z
        .string()
        .min(1, 'Captions file is empty')
        .max(MEDIA_ARCHIVE_CAPTIONS_MAX_LENGTH, 'Captions file is too large')
        .describe('Full text of a WebVTT (.vtt) file timed to the current recording'),
    filename: z
        .string()
        .trim()
        .min(1)
        .max(255)
        .optional()
        .describe('Original name of the file, kept with the upload; defaults to captions.vtt'),
});
